import { ButtonModal, Css, IconKey, StaticField, Typography } from "@homebound/beam";
import { Link } from "react-router-dom";
import { createDevelopmentLotDetailsUrl } from "src/RouteUrls";
import { OptionTypes } from "src/routes/developments/lot-summary/sequence-sheet/components/utils";
import {
  LotConfigurationDetails_GlobalOptionTypeFragment,
  LotConfigurationDetails_ProjectReadyPlanConfigOptionFragment,
  LotConfigurationDetails_ReadyPlanConfigFragment,
  useLotConfigurationDetailsQuery,
} from "src/routes/projects/components/LotConfigurationDetailsStaticField.generated";
import { groupBy } from "src/utils";
import { LotConfigurationSection } from "./LotConfigurationSection";
import { LotConfigurationStatusChip } from "./LotConfigurationStatusChip";

type LotConfigurationDetailsProps = {
  projectId: string;
  /** Layout config button and plan name as columns (default) or as row */
  layout?: "column" | "row";
  size?: Typography;
};

export function LotConfigurationDetailsStaticField({
  projectId,
  layout = "column",
  size = "xs2Sb",
}: LotConfigurationDetailsProps) {
  const { data, loading } = useLotConfigurationDetailsQuery({ variables: { projectId } });
  const isRowLayout = layout === "row";

  if (loading || !data) return <StaticField label="Plan" value="Loading..." />;

  if (isRowLayout)
    return (
      <div css={Css.df.fdr.gap1.aic.$}>
        <div css={Css.typography(size).gray700.$}>{data?.project.readyPlanConfig?.readyPlan?.displayName}</div>
        <div css={Css.typography(size).$}>
          {data.project.readyPlanConfig && (
            <ButtonModal
              content={
                <LotConfigurationDetailsDataView
                  readyPlanConfig={data.project.readyPlanConfig}
                  globalOptionTypes={data.globalOptionTypes}
                />
              }
              variant="text"
              hideEndAdornment
              trigger={{ label: "See lot configuration" }}
            />
          )}
        </div>
      </div>
    );

  return (
    <StaticField
      label={
        <div css={Css.df.aic.$}>
          <span css={Css.gray700.$}>Plan</span>
          <span css={Css.xs.ml1.$}>
            {data.project.readyPlanConfig?.options && (
              <ButtonModal
                content={
                  <LotConfigurationDetailsDataView
                    readyPlanConfig={data.project.readyPlanConfig}
                    globalOptionTypes={data.globalOptionTypes}
                  />
                }
                variant="text"
                hideEndAdornment
                trigger={{ label: "See lot configuration" }}
              />
            )}
          </span>
        </div>
      }
      value={
        data.project.readyPlanConfig?.readyPlan?.displayName
          ? data.project.readyPlanConfig?.readyPlan?.displayName
          : "N/A"
      }
    />
  );
}

function LotConfigurationDetailsDataView({
  readyPlanConfig,
  globalOptionTypes,
}: {
  readyPlanConfig: LotConfigurationDetails_ReadyPlanConfigFragment;
  globalOptionTypes: LotConfigurationDetails_GlobalOptionTypeFragment[];
}) {
  const grouped = groupBy(readyPlanConfig.options, ({ readyPlanOption }) => readyPlanOption.globalOption.type.name);
  // The v1 and v2 catalogs largely share type names, and we group options by name, so keep one section per name.
  const v1TypeNames = new Set(globalOptionTypes.filter((type) => type.version === 1).map(({ name }) => name));
  const validOptionTypes = globalOptionTypes
    .filter((type) => !type.isElevation)
    .filter((type) => !type.isPlanPackage)
    // Add-ons get a single section, sourced from both catalogs
    .filter((type) => type.name !== OptionTypes.AddOnV2)
    // v2-only types (Structural, personalization) have no v1 section to fall back on, so only show them when configured
    .filter((type) => (v1TypeNames.has(type.name) ? type.version === 1 : (grouped[type.name] ?? []).nonEmpty))
    .sortBy(({ order }) => order);
  return (
    <div css={Css.w("470px").$}>
      <div css={Css.df.jcsb.mb3.$}>
        <div css={Css.df.gap1.mb3.$}>
          <h3 css={Css.smSb.$}>Lot configuration</h3>
          <LotConfigurationStatusChip readyPlanConfig={readyPlanConfig} />
        </div>
        {readyPlanConfig.readyPlan?.__typename === "ReadyPlan" && readyPlanConfig.readyPlan.development && (
          <Link
            to={createDevelopmentLotDetailsUrl(readyPlanConfig.readyPlan.development.id, readyPlanConfig.id)}
            data-testid="link"
            css={Css.xs2.onFocusVisible.outline0.$}
          >
            View Details
          </Link>
        )}
      </div>
      <div css={Css.df.fdc.gap2.$}>
        {validOptionTypes.map((option) => (
          <div key={option.id} css={Css.bb.bcGray200.pb2.$}>
            <LotConfigurationSection
              icon={ICON_CONFIG[option.name] || "customize"}
              optionType={option.name}
              data={sectionOptions(option.name, grouped)}
              title={
                option.name === OptionTypes.AddOn
                  ? "Add-ons"
                  : option.name === OptionTypes.ExteriorScheme
                    ? "Exterior Options"
                    : `${option.name} Options`
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Sections that draw from more than one option type, i.e. elevation + scheme, or both add-on catalogs. */
function sectionOptions(
  typeName: string,
  grouped: Record<string, LotConfigurationDetails_ProjectReadyPlanConfigOptionFragment[]>,
) {
  switch (typeName) {
    case OptionTypes.ExteriorScheme:
      return [...(grouped[OptionTypes.Elevation] ?? []), ...(grouped[OptionTypes.ExteriorScheme] ?? [])];
    case OptionTypes.AddOn:
      return [...(grouped[OptionTypes.AddOn] ?? []), ...(grouped[OptionTypes.AddOnV2] ?? [])];
    default:
      return grouped[typeName];
  }
}

export const ICON_CONFIG: Record<string, IconKey> = {
  [OptionTypes.ExteriorScheme]: "leaf",
  [OptionTypes.FloorPlan]: "floorPlan",
  [OptionTypes.Interior]: "chair",
  [OptionTypes.AddOn]: "bolt",
};
